<template>
    <div class="w-full h-full">
        <v-chart ref="chart" class="price-chart" :option="option" />
    </div>
</template>

<script>
import { use, graphic } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { CandlestickChart, LineChart, BarChart } from 'echarts/charts';
import {
    TooltipComponent,
    GridComponent,
    GraphicComponent,
    AxisPointerComponent,
    MarkLineComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';
import watermark from '@/assets/DayTraderCanda.png';
import { sliceByRange } from '@/utils/candles';
import { FONT, chartPalette, rgba } from './Charts/theme';
import { date as fmtDate, price as fmtPrice, compact as fmtCompact, num as fmtNum, money as fmtMoney } from '@/utils/format';

use([
    CanvasRenderer,
    CandlestickChart,
    LineChart,
    BarChart,
    TooltipComponent,
    GridComponent,
    GraphicComponent,
    AxisPointerComponent,
    MarkLineComponent,
]);

const UP = '#00AA6F';
const DOWN = '#E5484D';
const EARNINGS = '#D97706';
// Upcoming reports are drawn only when they fall within this share of the
// visible span past the last candle (the axis is extended with empty sessions
// to reach them). 0 keeps the axis ending at the last candle: no empty gap.
const FUTURE_SHARE = 0;

/** Local date of "2026-10-28" (or an ISO timestamp), or null. */
function parseDay(v) {
    const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

function ymd(d) {
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Weekdays after `from` up to and including `to`, then `extra` more (yyyy-MM-dd). */
function weekdaysAfter(from, to, extra) {
    const out = [];
    const d = parseDay(from);
    const end = parseDay(to);
    if (!d || !end) return out;
    let past = 0;
    for (;;) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() === 0 || d.getDay() === 6) continue;
        if (d > end && ++past > extra) break;
        out.push(ymd(d));
    }
    return out;
}

export default {
    name: 'CandlestickChart',
    components: { VChart },
    props: {
        // Ascending daily candles: { timeStamp, open, high, low, close, volume }
        sampleDatas: { type: Array, default: () => [] },
        chartType: { type: String, default: 'candlestick' },
        range: { type: String, default: '1Y' },
        // Earnings calendar rows: { date, hour, epsActual, epsEstimate, ... }, past and future.
        earnings: { type: Array, default: () => [] },
    },
    computed: {
        locale() {
            return this.$i18n ? this.$i18n.locale : 'en';
        },
        slice() {
            return sliceByRange(this.sampleDatas, this.range);
        },
        // One row per report date, ascending.
        earningDates() {
            const today = ymd(new Date());
            const byDate = new Map();
            (Array.isArray(this.earnings) ? this.earnings : []).forEach((e) => {
                const d = parseDay(e && e.date);
                if (d) byDate.set(ymd(d), e);
            });
            return [...byDate.keys()].sort().map((date) => {
                const e = byDate.get(date);
                return {
                    date,
                    hour: e.hour,
                    epsActual: e.epsActual,
                    epsEstimate: e.epsEstimate,
                    upcoming: date >= today,
                };
            });
        },
        option() {
            const p = chartPalette();
            const rows = this.slice;
            const dates = rows.map((c) => String(c.timeStamp).slice(0, 10));
            const future = this.futureDates(dates);
            const axisDates = dates.concat(future);
            const pad = (list) => list.concat(future.map(() => '-'));
            const markers = this.earningMarkers(axisDates);
            const markerAt = new Map(markers.map((m) => [m.index, m]));
            const candles = pad(rows.map((c) => [c.open, c.close, c.low, c.high]));
            const closes = rows.map((c) => c.close);
            const volumes = pad(rows.map((c) => ({
                value: c.volume,
                itemStyle: { color: c.close >= c.open ? 'rgba(0,170,111,0.35)' : 'rgba(229,72,77,0.35)' },
            })));
            const first = closes[0];
            const last = closes[closes.length - 1];
            const lineColor = last >= first ? UP : DOWN;
            const shortRange = ['1M', '3M', '6M', 'YTD'].includes(this.range);
            const axisDate = (v) => fmtDate(v, this.locale, shortRange
                ? { month: 'short', day: 'numeric' }
                : { month: 'short', year: '2-digit' });

            const priceSeries = this.chartType === 'candlestick'
                ? {
                    name: 'price',
                    type: 'candlestick',
                    data: candles,
                    barMaxWidth: 10,
                    itemStyle: { color: UP, color0: DOWN, borderColor: UP, borderColor0: DOWN },
                }
                : {
                    name: 'price',
                    type: 'line',
                    data: pad(closes),
                    showSymbol: false,
                    smooth: false,
                    lineStyle: { width: 2, color: lineColor },
                    itemStyle: { color: lineColor },
                    areaStyle: this.chartType === 'area'
                        ? {
                            color: new graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: lineColor === UP ? 'rgba(0,170,111,0.22)' : 'rgba(229,72,77,0.20)' },
                                { offset: 1, color: rgba('surface', 0) },
                            ]),
                        }
                        : undefined,
                };
            if (markers.length) priceSeries.markLine = this.earningsMarkLine(markers);

            const axisLabel = { color: p.faint, fontSize: 11, fontFamily: FONT };

            return {
                animation: false,
                textStyle: { fontFamily: FONT },
                graphic: [{
                    type: 'image',
                    silent: true,
                    z: -10,
                    left: 'center',
                    top: '26%',
                    style: { image: watermark, width: 174, height: 76, opacity: 0.07 },
                }],
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'cross',
                        link: [{ xAxisIndex: 'all' }],
                        lineStyle: { color: p.faint, type: 'dashed' },
                        crossStyle: { color: p.faint, type: 'dashed' },
                        label: { backgroundColor: p.inverse, color: p.inverseInk, fontFamily: FONT, fontSize: 11 },
                    },
                    backgroundColor: p.surface,
                    borderColor: p.line,
                    borderWidth: 1,
                    padding: [10, 12],
                    extraCssText: `border-radius:12px;box-shadow:0 8px 24px ${p.shadow};`,
                    textStyle: { color: p.ink, fontFamily: FONT, fontSize: 12 },
                    formatter: (params) => this.tooltip(params, rows, axisDates, markerAt),
                },
                axisPointer: { link: [{ xAxisIndex: 'all' }] },
                grid: [
                    // Room above the plot for the earnings "E" badges.
                    { left: 12, right: 64, top: markers.length ? 30 : 16, height: '66%' },
                    { left: 12, right: 64, top: '78%', height: '14%' },
                ],
                xAxis: [
                    {
                        type: 'category',
                        data: axisDates,
                        boundaryGap: this.chartType === 'candlestick',
                        axisLine: { show: false },
                        axisTick: { show: false },
                        axisLabel: { show: false },
                        axisPointer: { label: { show: false } },
                    },
                    {
                        type: 'category',
                        gridIndex: 1,
                        data: axisDates,
                        boundaryGap: this.chartType === 'candlestick',
                        axisLine: { lineStyle: { color: p.line } },
                        axisTick: { show: false },
                        axisLabel: { ...axisLabel, formatter: axisDate, hideOverlap: true, alignMinLabel: 'left', alignMaxLabel: 'right' },
                        axisPointer: { label: { formatter: ({ value }) => fmtDate(value, this.locale) } },
                    },
                ],
                yAxis: [
                    {
                        scale: true,
                        position: 'right',
                        splitNumber: 5,
                        axisLine: { show: false },
                        axisTick: { show: false },
                        // Round ticks read cleaner without trailing zeros (250 not 250.00).
                        axisLabel: { ...axisLabel, formatter: (v) => (Number.isInteger(v) ? fmtNum(v, 0) : fmtPrice(v)) },
                        splitLine: { lineStyle: { color: p.lineSoft, type: 'dashed' } },
                        axisPointer: { label: { formatter: ({ value }) => fmtPrice(value) } },
                    },
                    {
                        scale: true,
                        gridIndex: 1,
                        position: 'right',
                        splitNumber: 2,
                        axisLine: { show: false },
                        axisTick: { show: false },
                        axisLabel: { ...axisLabel, formatter: (v) => fmtCompact(v, 0), showMinLabel: false },
                        splitLine: { show: false },
                        axisPointer: { label: { formatter: ({ value }) => fmtCompact(value) } },
                    },
                ],
                series: [
                    priceSeries,
                    {
                        name: 'volume',
                        type: 'bar',
                        xAxisIndex: 1,
                        yAxisIndex: 1,
                        data: volumes,
                        barMaxWidth: 10,
                    },
                    // Invisible points on report days: the axis tooltip only fires
                    // where some series has a value, which the future sessions lack.
                    {
                        name: 'earnings',
                        type: 'line',
                        data: axisDates.map((d, i) => (markerAt.has(i) ? last : '-')),
                        symbol: 'none',
                        lineStyle: { opacity: 0 },
                        silent: true,
                    },
                ],
            };
        },
    },
    mounted() {
        // vue-echarts' autoresize did not track the overlay's layout changes
        // (the chart kept the width it had when first drawn), so resize
        // explicitly whenever the container changes size.
        this.onResize = () => {
            const chart = this.$refs.chart;
            if (chart && chart.resize) chart.resize();
        };
        if (typeof ResizeObserver !== 'undefined') {
            this.observer = new ResizeObserver(() => this.onResize());
            this.observer.observe(this.$el);
        }
        window.addEventListener('resize', this.onResize);
        this.$nextTick(this.onResize);
    },
    beforeDestroy() {
        if (this.observer) this.observer.disconnect();
        window.removeEventListener('resize', this.onResize);
    },
    methods: {
        // Empty sessions past the last candle, far enough to reach the upcoming
        // reports that fit the horizon (plus a small margin so a badge is not
        // pinned to the right edge). None when no report qualifies.
        futureDates(dates) {
            const first = parseDay(dates[0]);
            const last = dates[dates.length - 1];
            if (!first || !last) return [];
            const horizon = parseDay(last);
            horizon.setDate(horizon.getDate() + Math.round(((horizon - first) / 86400000) * FUTURE_SHARE));
            const limit = ymd(horizon);
            const ahead = this.earningDates.filter((e) => e.date > last && e.date <= limit);
            if (!ahead.length) return [];
            return weekdaysAfter(last, ahead[ahead.length - 1].date, Math.max(2, Math.round(dates.length * 0.02)));
        },
        // Each report placed on the first session on or after its date (a report
        // on a holiday lands on the next session); reports off the axis are skipped.
        earningMarkers(axisDates) {
            const out = [];
            if (!axisDates.length) return out;
            const lastAxis = axisDates[axisDates.length - 1];
            let i = 0;
            this.earningDates.forEach((e) => {
                if (e.date < axisDates[0] || e.date > lastAxis) return;
                while (i < axisDates.length && axisDates[i] < e.date) i++;
                // Skip a report inside a long gap in the candles rather than
                // drawing it weeks away from where it happened.
                const gap = (parseDay(axisDates[i]) - parseDay(e.date)) / 86400000;
                if (i < axisDates.length && gap <= 4 && !out.some((m) => m.index === i)) out.push({ ...e, index: i });
            });
            return out;
        },
        earningsMarkLine(markers) {
            const p = chartPalette();
            const badge = (upcoming) => ({
                show: true,
                position: 'end',
                formatter: 'E',
                distance: 4,
                fontFamily: FONT,
                fontSize: 10,
                fontWeight: 600,
                lineHeight: 10,
                padding: [3, 4, 2, 4],
                borderRadius: 3,
                borderWidth: 1,
                borderColor: EARNINGS,
                // Reported: filled. Upcoming: outlined.
                color: upcoming ? EARNINGS : '#FFFFFF',
                backgroundColor: upcoming ? p.surface : EARNINGS,
            });
            return {
                silent: true,
                symbol: 'none',
                animation: false,
                data: markers.map((m) => ({
                    xAxis: m.index,
                    lineStyle: { color: EARNINGS, type: [2, 3], width: 1, opacity: m.upcoming ? 0.7 : 1 },
                    label: badge(m.upcoming),
                })),
            };
        },
        tooltip(params, rows, axisDates, markerAt) {
            const list = Array.isArray(params) ? params : [params];
            if (!list.length) return '';
            const index = list[0].dataIndex;
            const c = rows[index];
            const earning = markerAt.get(index);
            if (!c && !earning) return '';
            const t = (k) => this.$t(k);
            const p = chartPalette();
            const row = (label, value, color) =>
                `<div style="display:flex;justify-content:space-between;gap:18px;line-height:20px">` +
                `<span style="color:${p.muted}">${label}</span>` +
                `<span style="font-variant-numeric:tabular-nums;font-weight:500;color:${color || p.ink}">${value}</span></div>`;
            const day = (d) => fmtDate(d, this.locale, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
            let html = `<div style="font-weight:600;margin-bottom:6px">${day(axisDates[index])}</div>`;
            if (c) {
                const up = c.close >= c.open;
                const changePct = c.open ? ((c.close - c.open) / c.open) * 100 : null;
                html +=
                    row(t('Open'), fmtPrice(c.open)) +
                    row(t('High'), fmtPrice(c.high)) +
                    row(t('Low'), fmtPrice(c.low)) +
                    row(t('Close'), fmtPrice(c.close), up ? UP : DOWN) +
                    (changePct === null ? '' : row(t('Change'), `${changePct > 0 ? '+' : ''}${fmtNum(changePct)}%`, up ? UP : DOWN)) +
                    row(t('Volume'), fmtCompact(c.volume));
            }
            if (earning) html += this.earningTooltip(earning, axisDates[index], row, day, !!c);
            return html;
        },
        earningTooltip(e, axisDate, row, day, divided) {
            const t = (k) => this.$t(k);
            const p = chartPalette();
            const session = e.hour === 'amc' ? t('After close') : e.hour === 'bmo' ? t('Before open') : '';
            // The report date when the badge sits on a later session (holiday).
            const moved = e.date !== axisDate ? day(e.date) : '';
            const sub = [moved, session].filter(Boolean).join(' · ');
            const reported = e.epsActual !== null && e.epsActual !== undefined && e.epsActual !== '';
            const beat = reported && e.epsEstimate !== null && e.epsEstimate !== undefined
                ? (Number(e.epsActual) >= Number(e.epsEstimate) ? UP : DOWN)
                : null;
            return (
                `<div style="${divided ? `border-top:1px solid ${p.lineSoft};margin-top:8px;padding-top:8px;` : ''}">` +
                `<div style="display:flex;align-items:center;gap:6px;font-weight:600;line-height:20px">` +
                `<span style="display:inline-block;font-size:10px;line-height:10px;padding:3px 4px 2px;border-radius:3px;border:1px solid ${EARNINGS};` +
                `${e.upcoming ? `color:${EARNINGS};background:${p.surface}` : `color:#FFFFFF;background:${EARNINGS}`}">E</span>` +
                `${t(e.upcoming ? 'Upcoming earnings' : 'Earnings')}</div>` +
                (sub ? `<div style="color:${p.muted};line-height:18px;margin-bottom:2px">${sub}</div>` : '') +
                row(t('EPS estimate'), fmtMoney(e.epsEstimate)) +
                (reported ? row(t('Reported'), fmtMoney(e.epsActual), beat) : '') +
                `</div>`
            );
        },
    },
};
</script>

<style scoped>
.price-chart {
    height: 100%;
    width: 100%;
}
</style>
